import React, { FunctionComponent, ReactNode } from "react";
import { Button, ButtonPropsColorOverrides, SxProps } from "@mui/material";
import { OverridableStringUnion } from "@mui/types";

/**
 * Interface defining the props for the CustomButton component.
 * @property {ReactNode} [startIcon] - Icon element to display at the start of the button
 * @property {string} label - Text to display on the button
 * @property {boolean} [disabled=false] - Whether the button is disabled
 * @property {"button" | "submit"} [type] - The type of button
 * @property {SxProps} [style] - Additional styles to apply to the button
 * @property {() => void} [handleOnClick] - Click event handler
 * @property {OverridableStringUnion<"secondary" | "inherit" | "primary" | "success" | "error" | "info" | "warning", ButtonPropsColorOverrides>} [color] - The color of the button
 */
interface CustomButtonProps {
  startIcon?: ReactNode;
  label: string;
  disabled?: boolean;
  type?: "button" | "submit";
  style?: SxProps;
  handleOnClick?: () => void;
  color?: OverridableStringUnion<
    | "secondary"
    | "inherit"
    | "primary"
    | "success"
    | "error"
    | "info"
    | "warning",
    ButtonPropsColorOverrides
  >;
}

/**
 * A reusable custom button component built on Material-UI's Button.
 * 
 * This component provides a styled button with support for:
 * - Custom icons
 * - Multiple color variants
 * - Disabled state
 * - Custom styling
 * - Click handling
 * - Form submission
 * 
 * @component
 * @param {CustomButtonProps} props - The props passed to the component
 * @param {ReactNode} [props.startIcon] - Icon element to display
 * @param {string} props.label - Button text
 * @param {() => void} [props.handleOnClick] - Click handler
 * @param {OverridableStringUnion} [props.color="secondary"] - Button color
 * @param {"button" | "submit"} [props.type] - Button type
 * @param {boolean} [props.disabled=false] - Disabled state
 * @param {SxProps} [props.style={}] - Additional styles
 * 
 * @example
 * ```tsx
 * <CustomButton
 *   label="Submit"
 *   startIcon={<SaveIcon />}
 *   color="primary"
 *   type="submit"
 *   handleOnClick={() => handleSubmit()}
 * />
 * ```
 * 
 * @remarks
 * - Uses Material-UI Button component
 * - Supports all Material-UI color variants
 * - Maintains consistent styling with non-transformed text
 * - Supports custom styling through SxProps
 * - Default size is 'small'
 * - Default variant is 'contained'
 * 
 * @returns {JSX.Element} A React component that renders a custom button
 */
const CustomButton: FunctionComponent<CustomButtonProps> = (
  props: CustomButtonProps
) => {
  const {
    startIcon,
    label,
    handleOnClick,
    color,
    type,
    disabled = false,
    style = {},
  } = props;
  return (
    <>
      <Button
        startIcon={startIcon}
        color={color ? color : "secondary"}
        variant="contained"
        size="small"
        type={type}
        sx={{
          fontSize: "1rem",
          textTransform: "none",
          ...style,
        }}
        onClick={handleOnClick}
        disabled={disabled}
      >
        {label}
      </Button>
    </>
  );
};

export default CustomButton;
